import React, { useEffect, useMemo, useState } from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core";
import { Typography } from "@mui/material";
import { useIntl } from "react-intl";
import clipboard from "../assets/product-clipboard.svg";
import useClipboard from "../hooks/useClipBoard";
import ButtonComponent from "./common/ButtonComponent";
import { classicTheme } from "../styles/themes/classic";
import { CDN_WIDGET_URL, removeDotFromStart } from "utils/WidgetUtil";
import { parseInput, formatForClipboard } from "utils/ClipBoardUtils";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      marginTop: "10px",
    },
    textarea: {
      width: "100%",
      height: "100px ",
      overflowY: "auto",
      resize: "vertical",
      border: " 2px dashed",
      padding: "4px",
      borderColor: classicTheme.palette.tertiary.main,
      outline: "none",
      background: `url(${CDN_WIDGET_URL ? `${CDN_WIDGET_URL}${removeDotFromStart(clipboard)}` : clipboard}) no-repeat center center `,
      "&:focus": {
        borderColor: classicTheme.palette.tertiary.hoverColor, // Use the theme's primary color on hover
      },
    },
    textareaActions: {
      display: "flex",
      //gridTemplateColumns: "repeat(2,1fr) ",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 5,
    },
  }),
);

interface Props {
  onCollapse: (isCollapse: boolean) => void;
}

const ClipboardArea: React.FC<Props> = (props) => {
  const intl = useIntl();
  const { onCollapse } = props;
  const classes = useStyles();
  const { getClipboardProductCodes, setClipboardProductCodes, clearClipboard } =
    useClipboard();
  const productCodes = useMemo(
    () => getClipboardProductCodes() ?? [],
    [getClipboardProductCodes],
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    if (!isEditing) {
      setEditValue(formatForClipboard(productCodes));
    }
  }, [productCodes, isEditing]);

  useEffect(() => {
    if (productCodes.length > 0) {
      onCollapse(true);
    } else {
      onCollapse(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCodes]);

  const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const validInput = event.target.value;
    setIsEditing(true);
    setEditValue(validInput);
    if (validInput.trim() === "") {
      setClipboardProductCodes([]);
      onCollapse(false);
    } else {
      const codes = parseInput(validInput);
      setClipboardProductCodes(codes);
      onCollapse(true);
    }
  };

  const clearClickHandler = () => {
    clearClipboard();
    onCollapse(false);
  };

  const filteredCodes = productCodes.filter((codes) => codes !== "");
  return (
    <div className={classes.container}>
      <textarea
        className={classes.textarea}
        value={isEditing ? editValue : formatForClipboard(productCodes)}
        onBlur={() => setIsEditing(false)}
        onFocus={() => setIsEditing(true)}
        aria-label="Clipboard Area"
        onChange={changeHandler}
      />
      <div className={classes.textareaActions}>
        <Typography variant="inherit">
          {intl.formatMessage(
            {
              id: "clipBoardArea.skusText",
              defaultMessage: "{skus} skus",
            },
            {
              skus: filteredCodes?.length,
            },
          )}
        </Typography>
        <ButtonComponent
          color="secondaryButtonColorCTABlue"
          variant="outlined"
          justifyContent="flex-end"
          padding="0px 5px"
          onClick={clearClickHandler}
        >
          {intl.formatMessage({
            id: "clipBoardArea.clearLabel",
            defaultMessage: "Clear",
          })}
        </ButtonComponent>
      </div>
    </div>
  );
};

export default React.memo(ClipboardArea);
